import * as clack from '@clack/prompts'
import fs from 'node:fs'
import path from 'node:path'
import type { UpdateCommandOptions } from '@cli-types/core-engine/commands.js'
import type { TemplateEntry } from '@cli-types/next-adapter/commands.js'
import {
  COMPONENT_PACKAGE_DEPENDENCIES,
  STATIC_CUSTOM_DEPENDENCIES,
  TIPTAP_TEMPLATE_DEPENDENCIES
} from '@cli-constants/deps.js'
import { MAX_OVERWRITE_PREVIEW_ROWS } from '@cli-constants/limits.js'
import { TIPTAP_CONTENT_EDITOR_DIRECTORIES } from '@cli-constants/scaffolding.js'
import { removePath, resolveCliAssetPath } from '@cli-utils/paths.js'
import { isInteractiveSession, renderTableRows } from '@cli-utils/terminal.js'
import { applyAdminNamespaceToPath } from '@next-adapter/config/namespace/apply-admin-namespace-to-path.js'
import { resolveConfigOrExit } from '@next-adapter/config/resolve-config-or-exit.js'
import { syncInstalledIntegrationManifests } from '@next-adapter/integration-runtime/sync-installed-integration-manifests.js'
import { hasDeclaredPackage } from '@next-adapter/package-json/has-declared-package.js'
import { readProjectPackageJson } from '@next-adapter/package-json/read-project-package-json.js'
import { syncInstalledPresetManifests } from '@next-adapter/preset-runtime/sync-installed-preset-manifests.js'
import fsExtra from 'fs-extra'
import { applyNamespaceToTemplateEntry } from './apply-namespace-to-template-entry.js'
import { copyNamespacedDirectory } from './copy-namespaced-directory.js'
import { findStaticAssetFile } from './find-static-asset-file.js'
import { getAllComponentNamesForConfig } from './get-all-component-names-for-config.js'
import { getAllComponentNames } from './get-all-component-names.js'
import { getStaticCustomComponentEntries } from './get-static-custom-component-entries.js'
import { getStaticUiComponentEntries } from './get-static-ui-component-entries.js'
import { hasIntegration } from './has-integration.js'
import { hasPreset } from './has-preset.js'
import { normalizeShadcnPresetOnly } from './normalize-shadcn-preset-only.js'
import { runShadcnPresetUpdate } from './run-shadcn-preset-update.js'
import { TEMPLATE_REGISTRY } from './template-registry.js'
import { validateShadcnPresetOptions } from './validate-shadcn-preset-options.js'
import { writeNamespacedFile } from './write-namespaced-file.js'
export async function runUpdateCommand(
  components: string[],
  options: UpdateCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const normalizedOnly = normalizeShadcnPresetOnly(options.only)

  validateShadcnPresetOptions(components, options)

  if (options.json && !options.list) {
    clack.cancel('--json can only be used with --list.')
    process.exit(1)
  }

  if (options.list) {
    const uiComponents = getStaticUiComponentEntries()
    const customComponents = getStaticCustomComponentEntries()
    const templateKeys = Object.keys(TEMPLATE_REGISTRY).sort()
    const templatePath = (name: string): string => {
      const entry = TEMPLATE_REGISTRY[name]
      return entry.displayPath ?? (typeof entry.relPath === 'string' ? entry.relPath : '')
    }

    if (options.json) {
      const items = [
        ...uiComponents.map((component) => ({
          name: component.name,
          path: `components/ui/${component.file}`,
          kind: 'ui'
        })),
        ...customComponents.map((component) => ({
          name: component.name,
          path: `components/custom/${component.file}`,
          kind: 'custom'
        })),
        ...templateKeys.map((name) => ({ name, path: templatePath(name), kind: 'template' })),
        { name: 'tiptap', path: 'components/custom/content-editor/tiptap-*/', kind: 'special' }
      ]
      console.log(JSON.stringify(items, null, 2))
      return
    }

    const all = getAllComponentNames()
    clack.intro('Available components')

    clack.note(
      renderTableRows(
        uiComponents.map((component) => [component.name, `components/ui/${component.file}`])
      ).join('\n'),
      `Shadcn UI Components (${uiComponents.length})`
    )
    clack.note(
      renderTableRows(
        customComponents.map((component) => [component.name, `components/custom/${component.file}`])
      ).join('\n'),
      `Custom Components (${customComponents.length})`
    )
    clack.note(
      renderTableRows(templateKeys.map((name) => [name, templatePath(name)])).join('\n'),
      `Template Components (${templateKeys.length})`
    )
    clack.note(
      renderTableRows([['tiptap', 'components/custom/content-editor/tiptap-*/ (all files)']]).join(
        '\n'
      ),
      'Special'
    )

    clack.outro(`${all.length} components available`)
    return
  }

  const config = await resolveConfigOrExit(cwd)
  const admin = path.resolve(cwd, config.paths.admin)

  if (!fs.existsSync(admin)) {
    clack.cancel(
      `Admin directory not found at ${config.paths.admin}. Run 'betterstart init' first.`
    )
    process.exit(1)
  }

  if (options.shadcnPreset) {
    runShadcnPresetUpdate({
      cwd,
      config,
      preset: options.shadcnPreset.trim(),
      only: normalizedOnly
    })
    return
  }

  if (!options.all && components.length === 0) {
    clack.log.error(
      'Provide component names or use --all. Run with --list to see available components.'
    )
    process.exit(1)
  }

  clack.intro('BetterStart Update Components')

  const toUpdate = options.all ? getAllComponentNamesForConfig(config) : components
  const uiDir = resolveCliAssetPath('shared-assets', 'react-admin', 'ui')
  const customDir = resolveCliAssetPath('shared-assets', 'react-admin', 'custom')
  let updated = 0
  let skipped = 0
  const updatedTemplateNames = new Set<string>()
  const updatedStaticNames = new Set<string>()
  const requiredPackageDependencies = new Set<string>()
  const pendingWrites: Array<{ displayPath: string; write: () => void }> = []

  function trackPackageDependencies(name: string): void {
    for (const dependency of COMPONENT_PACKAGE_DEPENDENCIES[name] ?? []) {
      requiredPackageDependencies.add(dependency)
    }
  }

  function writeTemplateEntry(name: string, entry: TemplateEntry): boolean {
    if (updatedTemplateNames.has(name)) {
      return false
    }

    if (entry.requiredPreset && !hasPreset(config, entry.requiredPreset)) {
      clack.log.warn(`${name} requires the ${entry.requiredPreset} preset and was skipped.`)
      skipped++
      return false
    }
    if (entry.requiredIntegration && !hasIntegration(config, entry.requiredIntegration)) {
      clack.log.warn(
        `${name} requires the ${entry.requiredIntegration} integration and was skipped.`
      )
      skipped++
      return false
    }

    const { relPath, content } = applyNamespaceToTemplateEntry(entry, config, cwd)
    const baseDir = entry.base === 'cwd' ? cwd : admin
    const destPath = path.join(baseDir, relPath)
    if (entry.preserveExisting && fs.existsSync(destPath)) {
      clack.log.info(`Preserved ${relPath}`)
      updatedTemplateNames.add(name)
      skipped++
      return false
    }
    pendingWrites.push({
      displayPath: relPath,
      write: () => {
        fsExtra.ensureDirSync(path.dirname(destPath))
        fs.writeFileSync(destPath, content, 'utf-8')
        clack.log.success(`Updated ${relPath}`)
      }
    })
    updatedTemplateNames.add(name)
    trackPackageDependencies(name)
    updated++
    return true
  }

  function writeTemplateEntryWithDependencies(name: string, entry: TemplateEntry): boolean {
    const wroteEntry = writeTemplateEntry(name, entry)
    if (!wroteEntry) {
      return false
    }

    for (const dependencyName of entry.dependencies ?? []) {
      writeNamedDependency(dependencyName)
    }

    return true
  }

  function writeStaticAssetEntry(assetDirectory: 'ui' | 'custom', name: string): boolean {
    const key = `${assetDirectory}:${name}`
    if (updatedStaticNames.has(key)) {
      return false
    }

    const assetDir = assetDirectory === 'ui' ? uiDir : customDir
    const assetFile = findStaticAssetFile(assetDir, name)
    if (!assetFile) {
      return false
    }

    const namespace = config.frameworkConfig.next.namespace
    const namespacedAssetFile = applyAdminNamespaceToPath(assetFile, namespace)
    const destPath = path.join(admin, 'components', assetDirectory, namespacedAssetFile)
    pendingWrites.push({
      displayPath: `components/${assetDirectory}/${namespacedAssetFile}`,
      write: () => {
        fsExtra.ensureDirSync(path.dirname(destPath))
        writeNamespacedFile(path.join(assetDir, assetFile), destPath, namespace)
        clack.log.success(`Updated components/${assetDirectory}/${namespacedAssetFile}`)
      }
    })

    if (assetDirectory === 'custom') {
      const assetSubdir = path.join(assetDir, name)
      if (fs.existsSync(assetSubdir) && fs.statSync(assetSubdir).isDirectory()) {
        const namespacedName = applyAdminNamespaceToPath(name, namespace)
        const destSubdir = path.join(admin, 'components', assetDirectory, namespacedName)
        pendingWrites.push({
          displayPath: `components/${assetDirectory}/${namespacedName}/ (template files)`,
          write: () => {
            fsExtra.ensureDirSync(destSubdir)
            copyNamespacedDirectory(assetSubdir, destSubdir, namespace)
            clack.log.success(
              `Updated components/${assetDirectory}/${namespacedName}/ (template files)`
            )
          }
        })
      }
    }

    updatedStaticNames.add(key)
    trackPackageDependencies(name)
    updated++

    if (assetDirectory === 'custom') {
      for (const dependencyName of STATIC_CUSTOM_DEPENDENCIES[name] ?? []) {
        writeNamedDependency(dependencyName)
      }
    }

    return true
  }

  function writeTiptapTemplates(): boolean {
    const key = 'custom:tiptap'
    if (updatedStaticNames.has(key)) {
      return true
    }

    const srcBaseDir = resolveCliAssetPath(
      'shared-assets',
      'react-admin',
      'custom',
      'content-editor'
    )
    const namespace = config.frameworkConfig.next.namespace
    const destBaseDir = path.join(admin, 'components', 'custom', 'content-editor')
    if (!fs.existsSync(srcBaseDir)) {
      return false
    }

    const dirsToCopy: Array<{ srcDir: string; destDir: string }> = []
    for (const directory of TIPTAP_CONTENT_EDITOR_DIRECTORIES) {
      const srcDir = path.join(srcBaseDir, directory)
      if (!fs.existsSync(srcDir)) {
        continue
      }

      dirsToCopy.push({
        srcDir,
        destDir: path.join(destBaseDir, applyAdminNamespaceToPath(directory, namespace))
      })
    }

    if (dirsToCopy.length === 0) {
      return false
    }

    pendingWrites.push({
      displayPath: 'components/custom/content-editor/tiptap-*/ (template files)',
      write: () => {
        for (const { srcDir, destDir } of dirsToCopy) {
          fsExtra.ensureDirSync(destDir)
          copyNamespacedDirectory(srcDir, destDir, namespace)
        }
        clack.log.success('Updated components/custom/content-editor/tiptap-*/ (template files)')
        removePath(admin, path.join(admin, 'components', 'custom', 'tiptap'))
      }
    })
    updatedStaticNames.add(key)
    trackPackageDependencies('tiptap')
    updated++
    for (const dependencyName of TIPTAP_TEMPLATE_DEPENDENCIES) {
      writeNamedDependency(dependencyName)
    }
    return true
  }

  function writeNamedDependency(name: string): void {
    const entry = TEMPLATE_REGISTRY[name]
    if (entry) {
      writeTemplateEntryWithDependencies(name, entry)
      return
    }

    if (writeStaticAssetEntry('ui', name)) {
      return
    }

    if (writeStaticAssetEntry('custom', name)) {
      return
    }

    if (name === 'tiptap') {
      writeTiptapTemplates()
    }
  }

  for (const name of toUpdate) {
    if (TEMPLATE_REGISTRY[name]) {
      const entry = TEMPLATE_REGISTRY[name]
      writeTemplateEntryWithDependencies(name, entry)
      continue
    }

    if (writeStaticAssetEntry('ui', name)) {
      continue
    }

    if (writeStaticAssetEntry('custom', name)) {
      continue
    }

    if (name === 'tiptap') {
      if (!writeTiptapTemplates()) {
        clack.log.warn('tiptap templates not found')
        skipped++
      }
      continue
    }

    clack.log.warn(`Unknown component: ${name}`)
    skipped++
  }

  if (pendingWrites.length > 0) {
    const displayPaths = pendingWrites.map((entry) => entry.displayPath)
    const preview = displayPaths.slice(0, MAX_OVERWRITE_PREVIEW_ROWS)
    if (displayPaths.length > preview.length) {
      preview.push(`… and ${displayPaths.length - preview.length} more`)
    }
    clack.note(
      preview.join('\n'),
      `Overwriting ${displayPaths.length} file${displayPaths.length === 1 ? '' : 's'}`
    )

    if (!options.yes) {
      if (!isInteractiveSession()) {
        clack.log.error(
          `Overwriting ${displayPaths.length} file${displayPaths.length === 1 ? '' : 's'} needs confirmation. Re-run with --yes.`
        )
        process.exit(1)
      }

      const proceed = await clack.confirm({
        message: 'Overwrite these files with the latest templates?',
        initialValue: true
      })
      if (clack.isCancel(proceed) || !proceed) {
        clack.cancel('Update cancelled.')
        process.exit(0)
      }
    }

    for (const entry of pendingWrites) {
      entry.write()
    }
  }

  syncInstalledPresetManifests(cwd, config)
  syncInstalledIntegrationManifests(cwd, config)

  const projectPackageJson = readProjectPackageJson(cwd)
  const missingPackageDependencies = Array.from(requiredPackageDependencies).filter(
    (dependency) => !hasDeclaredPackage(projectPackageJson, dependency)
  )
  if (missingPackageDependencies.length > 0) {
    clack.log.warn(
      `Updated components require package dependencies that are not declared: ${missingPackageDependencies.join(', ')}. Run 'pnpm betterstart update-deps' before building.`
    )
  }

  clack.outro(
    `Updated ${updated} component${updated !== 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} skipped` : ''}`
  )
}
